/** @jsx jsx */
import {
  Flex,
  FullScreenModalProps,
  Modal,
  ModalActionButton,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  Paragraph,
} from "@eworkit/iris";
import { FC, useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { UserContext } from "@/components/User";
import { deleteApplication } from "@/data/job-applications";
import { useToasts } from "@/views/components/Toast/useToasts";

import { useApplyWizard } from "./ApplyWizardContext";

interface ApplyExitModalProps extends FullScreenModalProps {
  onSaved: () => void;
  onRemoved: () => void;
}

export const ApplyExitModal: FC<ApplyExitModalProps> = ({ onSaved, onRemoved, ...modalProps }) => {
  const { t } = useTranslation(["session", "common"]);
  const { addToast } = useToasts();
  const { contextCompany } = useContext(UserContext);

  const { draft, createOrUpdateDraft } = useApplyWizard();

  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const header = t("JOB_REQUESTS.APPLY.WIZARD.POPUP.TITLE");

  const handleSaveButtonClick = useCallback(async () => {
    try {
      setSaving(true);

      await createOrUpdateDraft(draft => draft);

      onSaved();
      modalProps.dialogState.hide();
    } catch (error) {
      addToast({
        text:
          error?.payload?.code === "INVALID_TYPE" || error?.payload?.code === "NOT_VALID_PARAM"
            ? t("JOB_REQUESTS.APPLY.WIZARD.WRONG_DATA_MESSAGE")
            : t("common:ERROR_MESSAGES.GENERAL"),
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  }, [createOrUpdateDraft, onSaved, modalProps, addToast, t]);

  const handleRemoveButtonClick = useCallback(async () => {
    try {
      setRemoving(true);

      if (draft?.id) {
        await deleteApplication({
          jobApplicationId: draft.id,
          companyId: contextCompany?.id,
        });
      }

      onRemoved();
      modalProps.dialogState.hide();
    } catch (error) {
      addToast({
        text: t("common:ERROR_MESSAGES.GENERAL"),
        type: "danger",
      });
    } finally {
      setRemoving(false);
    }
  }, [onRemoved, modalProps, addToast, contextCompany, draft, t]);

  return (
    <Modal aria-label={header} {...modalProps}>
      <ModalContainer>
        <ModalHeader
          onCloseClick={modalProps.dialogState.hide}
          closeButtonAriaLabel={t("COMPONENTS.MODAL.CLOSE")}
          headerPlaceholder={<ModalHeading aria-label={header}>{header}</ModalHeading>}
        />
        <ModalContent>
          <Flex flexDirection="column" gap="sm">
            <Paragraph>{t("JOB_REQUESTS.APPLY.WIZARD.POPUP.INFO")}</Paragraph>
          </Flex>
        </ModalContent>

        <ModalFooter>
          <ModalActionButton type="button" variant="ghost" onClick={modalProps.dialogState.hide}>
            {t("JOB_REQUESTS.APPLY.WIZARD.POPUP.BUTTON_CANCEL")}
          </ModalActionButton>

          <ModalActionButton
            type="button"
            variant="secondary-danger"
            onClick={handleRemoveButtonClick}
            disabled={removing}>
            {t("JOB_REQUESTS.APPLY.WIZARD.POPUP.BUTTON_REMOVE")}
          </ModalActionButton>

          <ModalActionButton type="button" variant="primary" onClick={handleSaveButtonClick} disabled={saving}>
            {t("JOB_REQUESTS.APPLY.WIZARD.POPUP.BUTTON_SAVE")}
          </ModalActionButton>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  );
};
